import * as React from 'react'
import { FormGroup, Position } from '@blueprintjs/core'
import { useDepopChannelFormContext } from '../../../../products_v4/listings/depop/DepopChannelFormContext'
import { ChannelPriceInput, SharedQuantityInput } from '../ChannelShared/SharedInputs'
import { DepopSpecificSelect } from './DepopSpecificSelect'
import CHANNELS from '../../../../../constants/Channels'
import CategoryMarketCategoryPickerV2 from '../../../../../components/Categories/CategoryMarketCategoryPickerV2'
import ListingDurationSelect from '../../../../../components/selects/channels/depop/ListingDurationSelect'
import { stylizeTitle, renderError } from '../Helpers'
import { Specifics } from './Specifics'
import { useDepopSpecifics } from './useDepopSpecifics'

// Depop has no title of its own — it derives one from the first line of the description — but the
// product title is still what we store and show, so cap it the way the other channels do.
export const DEPOP_MAX_TITLE_LENGTH = 140

interface Props {
  sectionOpen: boolean
  skipErrorPrefix?: boolean
}

export const DepopForm = React.memo((props: Props) => {
  const { skipErrorPrefix, sectionOpen } = props
  const { form, onListingChange, errors, productUnitTracking } = useDepopChannelFormContext()
  const { valuesByName } = useDepopSpecifics()

  const handleCategoryChange = React.useCallback(
    (v: number) => {
      onListingChange({ name: 'categoryMarketCategoryId', value: v })
    },
    [onListingChange]
  )

  const categoryError = skipErrorPrefix ? errors.categoryMarketCategoryId : errors.depopListingCategoryMarketCategoryId

  return (
    <div className="depop-form">
      <div className="stretch-full">
        <CategoryMarketCategoryPickerV2
          marketId={CHANNELS.DEPOP.id}
          style={undefined}
          label={stylizeTitle('Category', categoryError)}
          error={categoryError}
          showRequiredLabel
          value={form.categoryMarketCategoryId}
          onChange={handleCategoryChange}
          errorTooltipOffset={sectionOpen && 45}
          simpleValue
        />
      </div>
      <ChannelPriceInput
        error={skipErrorPrefix ? errors.price : errors.depopListingPrice}
        showRequiredLabel
        value={form.price}
        onListingChange={onListingChange}
        errorTooltipOffset={sectionOpen && 45}
      />
      <SharedQuantityInput
        error={skipErrorPrefix ? errors.quantity : errors.depopListingQuantity}
        minorStepSize={1}
        min={1}
        value={form.quantity}
        disabled={productUnitTracking}
        helperText={productUnitTracking ? 'Unit-tracking products automatically manage quantity' : ''}
        onListingChange={onListingChange}
        errorTooltipOffset={sectionOpen && 232}
        errorTooltipPosition={Position.LEFT}
      />
      <FormGroup
        label={stylizeTitle('Duration', skipErrorPrefix ? errors.duration : errors.depopListingDuration)}
        helperText={renderError(skipErrorPrefix ? errors.duration : errors.depopListingDuration)}
      >
        <ListingDurationSelect
          placeholder="Leave blank for indefinite"
          value={form.duration}
          onChange={value => onListingChange({ name: 'duration', value })}
        />
      </FormGroup>
      {/* Not ChannelSelectInput: it parseInts the selected value, which is fine for the numeric
          profile ids Etsy uses but turns a Depop condition into NaN. */}
      <DepopSpecificSelect
        specificName="Condition"
        value={form.condition}
        options={valuesByName.condition || []}
        required
        error={skipErrorPrefix ? errors.condition : errors.depopListingCondition}
        onChange={v => onListingChange({ name: 'condition', value: v })}
      />
      <Specifics />
    </div>
  )
})
